/**
 * Captcha
 * =======
 * Reusable Google reCAPTCHA v2 ("I'm not a robot" checkbox) wrapper for the
 * public auth forms (Login, Forgot Password). Lazy-loads the reCAPTCHA script
 * and renders the widget explicitly so it can be remounted per submit (v2
 * tokens are single-use).
 *
 * Renders nothing when CAPTCHA is disabled (or misconfigured) — callers gate on
 * the exported `captchaEnabled` flag.
 *
 * Callbacks:
 * - `onVerify(token)`  — fired with the response token when the user solves it.
 * - `onExpire()`       — fired when a solved token expires (clear it upstream).
 * - `onLoadError()`    — fired if the script is blocked / fails to load within
 *                        the ~8s timeout, so the form can ungate submit and show
 *                        a notice (the backend fails open).
 */

import React, { useEffect, useRef } from 'react';

const CAPTCHA_FLAG = process.env.REACT_APP_CAPTCHA_ENABLED === 'true';
const CAPTCHA_PROVIDER = process.env.REACT_APP_CAPTCHA_PROVIDER || 'recaptcha';
const CAPTCHA_SITE_KEY = process.env.REACT_APP_CAPTCHA_SITE_KEY || '';

// Enabled only when the flag is on, the provider is the wired one, and a site
// key is present — mirrors the backend's "enabled but misconfigured" guard.
export const captchaEnabled =
  CAPTCHA_FLAG && CAPTCHA_PROVIDER === 'recaptcha' && !!CAPTCHA_SITE_KEY;

const SCRIPT_ID = 'recaptcha-explicit-script';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
const LOAD_TIMEOUT_MS = 8000;
const POLL_INTERVAL_MS = 200;

// Inject the reCAPTCHA script tag exactly once.
function ensureScript() {
  if (document.getElementById(SCRIPT_ID)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

const Captcha = ({ onVerify, onExpire, onLoadError }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!captchaEnabled) return undefined;

    let cancelled = false;
    let pollId = null;
    const startedAt = Date.now();

    ensureScript();

    const tryRender = () => {
      if (cancelled) return;
      const grecaptcha = window.grecaptcha;
      if (grecaptcha && typeof grecaptcha.render === 'function' && containerRef.current) {
        try {
          grecaptcha.render(containerRef.current, {
            sitekey: CAPTCHA_SITE_KEY,
            callback: (token) => onVerify && onVerify(token),
            'expired-callback': () => onExpire && onExpire(),
            'error-callback': () => onLoadError && onLoadError(),
          });
        } catch (e) {
          if (onLoadError) onLoadError();
        }
        return;
      }
      // Script not ready yet — keep polling until the load timeout fires, so a
      // blocked script never strands the user behind a disabled submit button.
      if (Date.now() - startedAt > LOAD_TIMEOUT_MS) {
        if (onLoadError) onLoadError();
        return;
      }
      pollId = window.setTimeout(tryRender, POLL_INTERVAL_MS);
    };

    tryRender();

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
    };
  }, [onVerify, onExpire, onLoadError]);

  if (!captchaEnabled) return null;

  return <div ref={containerRef} className="flex justify-start" />;
};

export default Captcha;
